import {getTransform} from "ol/proj";
import {applyTransform, getWidth} from "ol/extent";
import {LineString, Point, Polygon} from "ol/geom";
import {fromExtent} from "ol/geom/Polygon";

/**
 * Transform an extent
 * @param extent {Array<number>} [minX,minY,maxX,maxY]
 * @param sourceProjCode {string} EPSG code from
 * @param targetProjCode {string} EPSG code to
 * @return {Array<number>}
 */
function transformExtent(extent, sourceProjCode, targetProjCode) {
    if (!extent || sourceProjCode === targetProjCode)
        return extent;
    const transformFunction = getTransform(sourceProjCode, targetProjCode);
    return applyTransform(extent, transformFunction);
}

function fixExtentWgs84(extent, maxExtent) {
    const simpleFix = (extent) => {
        extent = [
            Math.min(extent[0], extent[2]),
            Math.min(extent[1], extent[3]),
            Math.max(extent[0], extent[2]),
            Math.max(extent[1], extent[3])
        ];
        return [
            Math.max(-180, extent[0]),
            Math.max(-90, extent[1]),
            Math.min(180, extent[2]),
            Math.min(90, extent[3])
        ];
    };
    return fixExtent(simpleFix(extent),simpleFix(maxExtent))
}

function fixExtent(extent, maxExtent) {
    if (maxExtent) {
        extent = [
            Math.max(maxExtent[0], extent[0]),
            Math.max(maxExtent[1], extent[1]),
            Math.min(maxExtent[2], extent[2]),
            Math.min(maxExtent[3], extent[3])
        ];
    }
    return extent;
}

function expandExtent(extent, factor){
    const geom = fromExtent(extent)
    geom.scale(factor);
    return geom.getExtent();
}

function reducePrecisionWgs84(wgs84Coords) {
    return !wgs84Coords ? wgs84Coords : wgs84Coords.map(c => Number(c.toFixed(4)));
}

function formatWgs84(wgs84Coords) {
    return !wgs84Coords ? wgs84Coords : ('[' + reducePrecisionWgs84(wgs84Coords).join(', ') + ']');
}

function getBBoxFromUrl(paramName = 'bbox') {
    const url = new URL(window.location.href);
    return url.searchParams.has(paramName)
        ? url.searchParams.get(paramName).split(',').map(value => Number(value))
        : undefined;
}

function generatePointsCircle(count, center, resolution, circleDistanceMultiplier = 1, circleFootSeparation = 28, rings=1,circleStartAngle = Math.PI / 2) {
    rings=Math.max(rings,1);
    const circumference = circleDistanceMultiplier * circleFootSeparation * (2 + count);
    const radiusFull = Math.max(circumference / (Math.PI * 2), 35) * resolution;
    let ringPopulations = [];
    for(let i = rings ;i > 1  ; i--)
        ringPopulations.push(Math.round(count/(i*i)))
    ringPopulations = ringPopulations.filter(p=>p>3);
    ringPopulations.push(count-ringPopulations.reduce((partialSum, a) => partialSum + a, 0));

    const resPoints = [];
    for(let p = 0 ; p < ringPopulations.length ; p++) {
        const population = ringPopulations[p];
        const angleStep = (Math.PI * 2) / population;
        const radius = radiusFull/ringPopulations.length*(p+1);
        for (let i = 0; i < population; ++i) {
            const angle = circleStartAngle + i * angleStep;// Clockwise
            resPoints.push([
                center[0] + radius * Math.cos(angle),
                center[1] + radius * Math.sin(angle),
            ]);
        }
    }
    return {points: resPoints.map(p=>new Point(p)), lines: resPoints.map(p=>new LineString([p,center]))};
}

function getLargestPolygon(geom){
    let polys = [];
    if (geom.getType() === 'GeometryCollection') {
        polys = geom.getGeometries().concat(geom.getGeometries().filter(g => g.getType() === 'Polygon'));
        geom.getGeometries()
            .filter(g => g.getType() === 'MultiPolygon')
            .forEach(mp => polys = polys.concat(mp.getPolygons()));
    } else if (geom.getType() === 'Polygon')
        polys.push(geom);
    else if (geom.getType() === 'MultiPolygon')
        polys = geom.getPolygons();

    let geometry = undefined, widest = 0;
    for (let i = 0; i < polys.length; i++) {
        let width = getWidth(polys[i].getExtent());
        if (width > widest) {
            widest = width;
            geometry = polys[i];
        }
    }
    return geometry;
}


if (!window.tumbo)
    window.tumbo = {}
window.tumbo.transformExtent = transformExtent;
export {transformExtent, fixExtentWgs84, fixExtent, expandExtent, reducePrecisionWgs84, formatWgs84, getBBoxFromUrl, generatePointsCircle, getLargestPolygon}